// components/WeightSlider.tsx
import React from 'react';
import {
  Box,
  Text,
  Flex,
  HStack,
  VStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Icon,
  Tooltip
} from '@chakra-ui/react';
import { Info } from 'lucide-react';

interface WeightSliderProps {
  id: string;
  name: string;
  description: string;
  value: number;
  color: string;
  onChange: (value: number) => void;
  disabled?: boolean;
  examples?: {
    low: string;
    high: string;
  };
}

export const WeightSlider: React.FC<WeightSliderProps> = ({
  id,
  name,
  description,
  value,
  color,
  onChange,
  disabled = false,
  examples
}) => {
  const formatMultiplier = (value: number) => {
    return Number(value * 100).toFixed(0) + '%';
  };

  return (
    <Box key={id} display="flex" flexDirection="column" gap={2}>
      <Flex justify="space-between" align="center">
        <HStack spacing={2}>
          <Text fontWeight="medium" fontSize={'14px'}>
            {name}
          </Text>
          <Tooltip 
            label={
              <VStack align="start" spacing={1} maxW="300px">
                <Text fontSize="sm" fontWeight="medium">{description}</Text>
                {examples && (
                  <>
                    <Text fontSize="xs" color="gray.200">Examples:</Text>
                    <Text fontSize="xs">• Low: {examples.low}</Text>
                    <Text fontSize="xs">• High: {examples.high}</Text>
                  </>
                )}
              </VStack>
            }
            placement="top"
            hasArrow
          >
            <Icon as={Info} color="gray.400" cursor="help" />
          </Tooltip>
        </HStack>
        <Text fontWeight="medium" fontSize={'14px'}>
          {formatMultiplier(value)}
        </Text>
      </Flex>
      <HStack spacing={3}>
        <Slider
          value={value}
          min={0.5}
          max={1.5}
          step={0.01}
          onChange={onChange}
          isDisabled={disabled}
        >
          <SliderTrack bg="gray.200" borderRadius="full">
            <SliderFilledTrack bgGradient="linear(to-r, #C8E6C9 10%, #4CAF50 50%, #204A22 100%)" />
          </SliderTrack>
          {!disabled && (
            <SliderThumb
              boxSize={5}
              bg="white"
              border="2px solid"
              borderColor="green.400"
              shadow="lg"
              top="50%"
              transform="translateY(-50%)"
              _focus={{
                boxShadow: '0 0 0 3px rgba(72, 187, 120, 0.3)',
                transform: 'translateY(-50%)'
              }}
              _active={{
                transform: 'translateY(-50%) scale(1.1)'
              }}
              _hover={{
                transform: 'translateY(-50%) scale(1.05)'
              }}
            />
          )}
        </Slider>
      </HStack>
      <Box className="flex justify-between">
        <Text fontSize="xs" color="gray.500">
          Low
        </Text>
        <Text fontSize="xs" color="green.900">
          Default
        </Text>
        <Text fontSize="xs" color="gray.500">
          More
        </Text>
      </Box>
    </Box>
  );
};
